import { userToken } from '@/features/auth/helpers';
import { getSession } from 'next-auth/react';

import { ApiError, HttpStatusCode } from './ApiError';

export const SERVER_ORIGIN = process.env.ORIGIN ?? '';

export const BASE_URL =
  globalThis.window === undefined ? process.env.NEXT_PUBLIC_BASE_URL_SERVER : process.env.NEXT_PUBLIC_BASE_URL;

export const BASE_API_URL = `${BASE_URL}/api`;
export const ORIGIN =
  globalThis.window !== undefined && globalThis.location.origin != null ? globalThis.location.origin : '';

export const REPO_URL = process.env.NEXT_PUBLIC_REPO_URL;

export const baseHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
  'Access-Control-Allow-Origin': ORIGIN,
};

type ParamValue = string | number | boolean | null | undefined;
type ParamRecord = Record<string, ParamValue | ParamValue[]>;

const stringfyBody = (body: unknown) => (body ? JSON.stringify(body) : undefined);

export const buildURLWithParams = (url: string, params: ParamRecord) => {
  const filteredParams = Object.fromEntries(
    Object.entries(params)
      .filter(
        (entry): entry is [string, string | number | boolean | (string | number | boolean)[]] =>
          entry[1] !== null && entry[1] !== undefined,
      )
      .map(([key, value]) => [key, String(value)]),
  );
  return `${url}?${new URLSearchParams(filteredParams).toString()}`;
};

export async function parseResponse(response: Response) {
  if (!response.ok) {
    let message = response.statusText;
    try {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const error = await response.json();

        message = error.detail ?? error.title ?? error.message ?? message;
      } else {
        const text = await response.text();
        if (text.includes('<!DOCTYPE') || text.includes('<html')) {
          message = `${response.statusText} (Server returned HTML instead of JSON)`;
        } else {
          message = text || response.statusText;
        }
      }
    } catch {
      // ignore parse error
    }
    throw new ApiError(response.status, message);
  }
  const string = await response.text();
  return string === '' ? {} : JSON.parse(string);
}

async function fetchApi(fixedURL: string, requestHeaders: Record<string, string>, config?: RequestInit) {
  try {
    const response = await fetch(`${BASE_URL}/api${fixedURL}`, {
      headers: requestHeaders,
      ...config,
    });
    return await parseResponse(response);
  } catch (error: unknown) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof Error) {
      throw new ApiError(error);
    }
    throw new ApiError(HttpStatusCode.InternalServerError, String(error));
  }
}

// Cache the client session to avoid repeated /api/auth/session calls
let cachedClientSession: { token: string; timestamp: number } | null = null;
const CLIENT_SESSION_TTL = 60_000; // 1 minute

export async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = { ...baseHeaders };

  if (globalThis.window === undefined) {
    const token = await userToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  } else {
    const now = Date.now();
    if (cachedClientSession && now - cachedClientSession.timestamp < CLIENT_SESSION_TTL) {
      headers.Authorization = `Bearer ${cachedClientSession.token}`;
    } else {
      const session = await getSession({ broadcast: false });
      if (session?.accessToken != null) {
        headers.Authorization = `Bearer ${session.accessToken}`;
        cachedClientSession = { token: session.accessToken, timestamp: now };
      }
    }
  }

  return headers;
}

export function normalizeUrl(url: string): string {
  return url.startsWith('/') ? url : `/${url}`;
}

export async function request<TResponse>(url: string, config?: RequestInit): Promise<TResponse> {
  const requestHeaders = await getAuthHeaders();
  const mergedHeaders = { ...requestHeaders, ...Object.fromEntries(new Headers(config?.headers)) };
  const bodyIsFormData = config?.body instanceof FormData;
  if (bodyIsFormData) {
    for (const k of Object.keys(mergedHeaders)) {
      if (k.toLowerCase() === 'content-type') delete mergedHeaders[k];
    }
  } else if (config?.body && !('Content-Type' in mergedHeaders)) {
    const hasContentType = Object.keys(mergedHeaders).some((k) => k.toLowerCase() === 'content-type');
    if (!hasContentType) mergedHeaders['Content-Type'] = 'application/json';
  }

  const finalConfig: RequestInit = {
    ...config,
    headers: mergedHeaders,
  };
  return await fetchApi(normalizeUrl(url), mergedHeaders, finalConfig);
}

const api = {
  get: async <TResponse>(url: string, config?: RequestInit) => await request<TResponse>(url, config),

  post: async <TResponse>(url: string, body?: unknown, config?: RequestInit) =>
    await request<TResponse>(url, {
      ...config,
      method: 'POST',
      body: body instanceof FormData ? body : stringfyBody(body),
    }),

  patch: async <TResponse>(url: string, body?: unknown, config?: RequestInit) =>
    await request<TResponse>(url, {
      ...config,
      method: 'PATCH',
      body: body instanceof FormData ? body : stringfyBody(body),
    }),

  put: async <TResponse>(url: string, body?: unknown, config?: RequestInit) =>
    await request<TResponse>(url, {
      ...config,
      method: 'PUT',
      body: body instanceof FormData ? body : stringfyBody(body),
    }),

  delete: async <TResponse>(url: string, body?: unknown, config?: RequestInit) =>
    await request<TResponse>(url, {
      ...config,
      method: 'DELETE',
      body: body instanceof FormData ? body : stringfyBody(body),
    }),

  downloadFile: async (url: string, body?: unknown, config?: RequestInit) => {
    const requestHeaders = await getAuthHeaders();
    const bodyIsFormData = body instanceof FormData;

    const headers = { ...requestHeaders, ...Object.fromEntries(new Headers(config?.headers)) };
    if (bodyIsFormData) {
      for (const k of Object.keys(headers)) {
        if (k.toLowerCase() === 'content-type') delete headers[k];
      }
    }
    const response = await fetch(`${BASE_URL}/api${normalizeUrl(url)}`, {
      ...config,
      method: 'POST',
      headers,
      body: bodyIsFormData ? body : stringfyBody(body),
    });

    if (!response.ok) {
      let message = response.statusText;
      try {
        const text = await response.text();
        message = text || response.statusText;
      } catch {
        /* ignore */
      }
      throw new ApiError(response.status, message);
    }

    const contentDisposition = response.headers.get('content-disposition');
    const filenameMatch = contentDisposition?.match(/filename="?([^";]+)"?/);
    const filename = filenameMatch ? filenameMatch[1] : 'download.xlsx';

    const blob = await response.blob();
    return { blob, filename };
  },

  isCancel: (error: unknown) =>
    error instanceof ApiError && error.cause instanceof Error && error.cause.name === 'AbortError',
};

export default api;
